
import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from '@/components/ui/use-toast';

type MapboxTokenContextType = {
  mapboxToken: string | null;
  setMapboxToken: (token: string) => void;
  clearMapboxToken: () => void;
};

const STORAGE_KEY = 'mapbox-token';
const DEFAULT_MAPBOX_TOKEN = 'pk.eyJ1IjoiZGtsdW0iLCJhIjoiY203dGlmNzFxMDYzMTJqb2xoZDgwc21xOSJ9.TZE698bDEpB5T-KDxZViZg';

const MapboxTokenContext = createContext<MapboxTokenContextType | undefined>(undefined);

export const MapboxTokenProvider = ({ children }: { children: React.ReactNode }) => {
  const [mapboxToken, setMapboxTokenState] = useState<string | null>(() => {
    try {
      // Try to get token from localStorage on initial load
      const storedToken = localStorage.getItem(STORAGE_KEY);
      // Use default token if no stored token exists
      return storedToken || DEFAULT_MAPBOX_TOKEN;
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      // Fallback to default token if localStorage fails
      return DEFAULT_MAPBOX_TOKEN;
    }
  });

  // Validate the token on mount
  useEffect(() => {
    const validateToken = (token: string) => {
      // Use a simple image request for validation
      const img = new Image();
      const timestamp = Date.now();
      img.src = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/0,0,1/1x1?access_token=${token}&cacheBuster=${timestamp}`;
      
      img.onload = () => {
        console.log(`Token validation successful`);
        // Ensure token is saved to localStorage if it's valid
        localStorage.setItem(STORAGE_KEY, token);
      };
      
      img.onerror = () => {
        console.error(`Token validation failed`);
        // If default token doesn't work, clear it to prompt user for a new one
        if (token === DEFAULT_MAPBOX_TOKEN) {
          localStorage.removeItem(STORAGE_KEY);
          setMapboxTokenState(null);
          toast({
            title: "Default Token Error",
            description: "The default Mapbox token is not working. Please provide a valid token.",
            variant: "destructive",
          });
        }
      };
    };

    if (mapboxToken) {
      validateToken(mapboxToken);
    }
  }, [mapboxToken]);

  useEffect(() => {
    // Set default token if none exists
    if (!mapboxToken) {
      try {
        localStorage.setItem(STORAGE_KEY, DEFAULT_MAPBOX_TOKEN);
        setMapboxTokenState(DEFAULT_MAPBOX_TOKEN);
        console.log('Default Mapbox token set');
      } catch (error) {
        console.error('Error storing default mapbox token:', error);
      }
    } else {
      console.log('Using Mapbox token:', mapboxToken.substring(0, 10) + '...');
    }
  }, [mapboxToken]);

  const setMapboxToken = (token: string) => {
    try {
      if (!token || token.trim() === '') {
        toast({
          title: "Invalid Token",
          description: "The Mapbox token cannot be empty",
          variant: "destructive",
        });
        return;
      }
      
      // Store token in localStorage for persistence
      localStorage.setItem(STORAGE_KEY, token);
      setMapboxTokenState(token);
      console.log('Mapbox token set successfully');
      toast({
        title: "Success",
        description: "Mapbox token has been saved",
      });
      
      // Force page reload to reinitialize the map with the new token
      window.location.reload();
    } catch (error) {
      console.error('Error storing mapbox token:', error);
      toast({
        title: "Error",
        description: "Failed to save Mapbox token",
        variant: "destructive",
      });
    }
  };

  const clearMapboxToken = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setMapboxTokenState(null);
      console.log('Mapbox token cleared');
      toast({
        title: "Token Cleared",
        description: "Mapbox token has been removed",
      });
    } catch (error) {
      console.error('Error clearing mapbox token:', error);
      toast({
        title: "Error",
        description: "Failed to clear Mapbox token",
        variant: "destructive",
      });
    }
  };

  return (
    <MapboxTokenContext.Provider value={{ mapboxToken, setMapboxToken, clearMapboxToken }}>
      {children}
    </MapboxTokenContext.Provider>
  );
};

export const useMapboxToken = () => {
  const context = useContext(MapboxTokenContext);
  if (context === undefined) {
    throw new Error('useMapboxToken must be used within a MapboxTokenProvider');
  }
  return context;
};
